import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { VehicleCard } from "./VehicleCard";
import { VehicleDialog } from "./VehicleDialog";
import { PartCard, PartDialog } from "./PartCard";
import { vehicleTexts } from "./vehicleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MoreLink = ({ to, label, testid }) => (
  <Reveal delay={0.2}>
    <Link to={to} data-testid={testid} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-hover">
      {label}<ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
    </Link>
  </Reveal>
);

const VehiclesSection = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const tv = t.vehicles;
  const vt = vehicleTexts(settings, tv);
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(null);
  const [openPart, setOpenPart] = useState(null);
  useEffect(() => {
    axios.get(`${API}/vehicles?limit=3`).then((r) => setItems(r.data)).catch(() => {});
    axios.get(`${API}/parts-market?limit=3`).then((r) => setParts(r.data)).catch(() => {});
  }, []);
  if (items.length === 0 && parts.length === 0) return null;
  const hasWanted = parts.some((p) => p.kind === "part_wanted");

  return (
    <section className="bg-cream/50 py-24 sm:py-32" data-testid="vehicles-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {items.length > 0 && (
          <>
            <div className="mb-14 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <SectionHead overline={vt.overline} title={vt.homeTitle} accent={vt.homeAccent} />
              <MoreLink to="/vehicules" label={tv.all} testid="vehicles-all-link" />
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {items.map((v, i) => <Reveal key={v.id} delay={0.1 * i}><VehicleCard v={v} index={i} onOpen={setOpen} /></Reveal>)}
            </div>
          </>
        )}
        {parts.length > 0 && (
          <div className={items.length > 0 ? "mt-20 border-t border-brand/10 pt-16" : ""} data-testid="home-parts">
            <div className="mb-10 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <SectionHead overline={tv.parts.homeOverline} title={tv.parts.homeTitle} accent={hasWanted ? tv.parts.homeAccentWanted : tv.parts.homeAccent} sub={tv.parts.homeSub} />
              <MoreLink to="/vehicules?type=part_sale" label={tv.parts.all} testid="home-parts-all-link" />
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {parts.map((p, i) => <Reveal key={p.id} delay={0.1 * i}><PartCard p={p} index={i} onOpen={setOpenPart} /></Reveal>)}
            </div>
          </div>
        )}
      </div>
      <VehicleDialog vehicle={open} onClose={() => setOpen(null)} />
      <PartDialog part={openPart} onClose={() => setOpenPart(null)} />
    </section>
  );
};

export default VehiclesSection;
